import axios from 'axios';
import { REST_HOSTNAME } from '../Config';
import { ScoreGrade } from '../Score';
import { ISentenceScore } from './ScoreApi';

const axiosInstance = axios.create({
    baseURL: REST_HOSTNAME
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            config.headers['x-access-token'] = jwt; // Add JWT to the headers
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

export enum ManualGradeEnum {
    NoProblem = "no_problem",
    MinorProblem = "minor_problem",
    NeedFixes = "need_fixes",
    NothingSpoken = "nothing_spoken",
}
export enum AlignmentErrorEnum {
    Deletion = "deletion",
    Insertion = "insertion",
    None = "none"
}

export interface ISentenceScoreManualIndividualGrade {
    id: number,
    sentence_score_manual_grade_id: number,
    index: number,
    score_grade: ScoreGrade | null,
    initial_score_grade: ScoreGrade | null,
    final_score_grade: ScoreGrade | null,
    tone_score_grade: ScoreGrade | null
    alignment_error: AlignmentErrorEnum | null,
    transcript_char: string,
    pinyin_spoken: string,
    pinyin_transcript: string
}

export interface ISentenceScoreManualGrade {
    id: number;
    sentence_score_id: number;
    user_id: number;
    manual_grade: ManualGradeEnum | null;
    has_extra_or_missing: boolean | null;
    incorrect_word_count: number | null;
    sentence_score_manual_individual_grade_list: ISentenceScoreManualIndividualGrade[] | null;
    sentence_score?: ISentenceScore;
    created_at: Date | null,
    updated_at: Date | null,

}

export const SentenceScoreManualGradeApi = {
    getAll: function () {
        return axiosInstance.request<ISentenceScoreManualGrade[]>({
            method: "GET",
            url: `/sentence_score_manual_grade`,
            transformResponse: [(data) => {
                const raw = typeof data === 'string' ? JSON.parse(data) : data;
                return (raw as any[]).map(item => ({
                    ...item,
                    created_at: item.created_at == null ? item.created_at : new Date(item.created_at),
                    updated_at: item.updated_at == null ? item.updated_at : new Date(item.updated_at),
                }));
            }],
        });
    },
    getById: function (sentenceScoreManualGradeId: number) {
        return axiosInstance.request<ISentenceScoreManualGrade>({
            method: "GET",
            url: `/sentence_score_manual_grade/${sentenceScoreManualGradeId}`,
            transformResponse: [(data) => {
                const item = typeof data === 'string' ? JSON.parse(data) : data;
                return ({
                    ...item,
                    created_at: item.created_at == null ? item.created_at : new Date(item.created_at),
                    updated_at: item.updated_at == null ? item.updated_at : new Date(item.updated_at),
                });
            }],

        });
    },
    create: function (sentenceScoreManualGrade: ISentenceScoreManualGrade) {
        return axiosInstance.request<ISentenceScoreManualGrade>({
            method: "POST",
            url: `/sentence_score_manual_grade`,
            data: sentenceScoreManualGrade,
        });
    },
    update: function (sentenceScoreManualGrade: ISentenceScoreManualGrade) {
        return axiosInstance.request<string>({
            method: "PUT",
            url: `/sentence_score_manual_grade`,
            data: sentenceScoreManualGrade
        });
    },
    delete: function (sentenceScoreManualGradeId: number) {
        return axiosInstance.request<string>({
            method: "DELETE",
            url: `/sentence_score_manual_grade/${sentenceScoreManualGradeId}`,
        });
    },
    getRandom: function (courseId?: number) {
        return axiosInstance.request<ISentenceScoreManualGrade>({
            method: "GET",
            url: `/sentence_score_manual_grade/random`,
            params: courseId ? { course_id: courseId } : undefined
        });
    },
    getPage: function (page: number, pageSize: number, sort: string = 'id', order: 'asc' | 'desc' = 'desc') {
        return axiosInstance.request<PaginatedResponse<ISentenceScoreManualGrade>>({
            method: "GET",
            url: `/sentence_score_manual_grade/page`,
            params: { page, page_size: pageSize, sort, order }
        });
    },
}

export type PaginatedResponse<T> = {
    items: T[];
    total: number;
    page: number;
    page_size: number;
    has_next?: boolean;
};